import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faArrowUp,
  faArrowDown,
  faMinus,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import SalesEvolutionChart from "components/charts/e-charts/SalesEvolutionChart";
import { useBreakpoints } from "providers/BreakpointsProvider";
import { useEmpresas } from "providers/EmpresaProvider";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { formatCurrency, formatPercentage } from "utils/formatters";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { DashCard } from "./v2/DashCard";
import DashStatTile from "./v2/DashStatTile";
import DashSegmentedControl from "./v2/DashSegmentedControl";

type PeriodType = "annual" | "semiannual" | "monthly";

interface SalesMetrics {
  currentValue: number;
  growthPercentage: number;
  isPositiveGrowth: boolean;
  bestPeriod: string;
  bestPeriodValue: number;
  trend: "up" | "down" | "stable";
}

interface SalesEvolutionData {
  labels: string[];
  values: number[];
  comparisonValues: number[];
  growthPercentage: number;
  isPositiveGrowth: boolean;
}

const PERIOD_OPTIONS: { key: PeriodType; label: string; mapped: string }[] = [
  { key: "monthly", label: "Mensal", mapped: "month" },
  { key: "semiannual", label: "Semestral", mapped: "semester" },
  { key: "annual", label: "Anual", mapped: "year" },
];

const PERIOD_DESCRIPTION: Record<PeriodType, string> = {
  monthly: "Últimos 12 meses",
  semiannual: "Últimos 4 semestres",
  annual: "Últimos 5 anos",
};

const SalesEvolution = () => {
  const [period, setPeriod] = useState<PeriodType>("monthly");
  const { breakpoints } = useBreakpoints();
  const { selectedIds } = useEmpresas();
  const { getQueryRetryHandler } = usePermissionModal();

  const isXs = breakpoints.down("sm");
  const hasMultipleEmpresas = selectedIds.length > 1;
  const mappedPeriod = PERIOD_OPTIONS.find((p) => p.key === period)!.mapped;

  const { data: chartData, isLoading: chartLoading, error } = useQuery({
    queryKey: ["salesEvolution", period, selectedIds],
    queryFn: async (): Promise<SalesEvolutionData> => {
      const response = await axios.get(`/api/SalesArea/sales-evolution?period=${mappedPeriod}`);
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["salesEvolution", period, selectedIds],
      defaultMessage: "Você não possui permissão para visualizar a evolução de vendas.",
      showModal: false,
    }),
  });

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  const metrics: SalesMetrics | null = chartData
    ? (() => {
        const { labels, values, growthPercentage, isPositiveGrowth } = chartData;
        if (!values || values.length === 0) return null;

        const maxValueIndex = values.indexOf(Math.max(...values));
        const bestPeriod = labels[maxValueIndex] || "N/A";
        const bestPeriodValue = values[maxValueIndex] || 0;
        const currentValue = values.reduce((sum, val) => sum + val, 0);

        const lastThree = values.slice(-3);
        let trend: SalesMetrics["trend"] = "stable";
        if (lastThree.length >= 2) {
          const first = lastThree[0];
          const last = lastThree[lastThree.length - 1];
          const percentChange = first !== 0 ? Math.abs(((last - first) / first) * 100) : 0;
          if (percentChange > 5) trend = last > first ? "up" : "down";
        }

        return { currentValue, growthPercentage, isPositiveGrowth, bestPeriod, bestPeriodValue, trend };
      })()
    : null;

  const trendIcon = metrics?.trend === "up" ? faArrowUp : metrics?.trend === "down" ? faArrowDown : faMinus;
  const trendVariant = metrics?.trend === "up" ? "good" : metrics?.trend === "down" ? "critical" : "accent";
  const trendLabel = metrics?.trend === "up" ? "Crescimento" : metrics?.trend === "down" ? "Declínio" : "Estável";

  return (
    <div>
      <DashCard hoverable={false} className="mb-3">
        <div className="dash-card-body" style={{ padding: "1rem 1.25rem" }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-start gap-3">
              <div className="dash-card-icon dash-card-icon--accent">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div>
                <h6 className="dash-card-title">Evolução de Vendas</h6>
                <p className="dash-card-subtitle">
                  {PERIOD_DESCRIPTION[period]}
                  {hasMultipleEmpresas && (
                    <span className="dash-pill dash-pill--neutral ms-2">
                      {selectedIds.length} empresas
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faCalendarDays} style={{ color: "var(--dash-muted)", fontSize: "0.8rem" }} />
              <DashSegmentedControl
                value={period}
                onChange={setPeriod}
                options={PERIOD_OPTIONS.map((opt) => ({
                  value: opt.key,
                  label: isXs ? opt.label.slice(0, 3) : opt.label,
                }))}
              />
            </div>
          </div>
        </div>
      </DashCard>

      {(metrics || chartLoading) && (
        <div className="dash-grid dash-grid-4 mb-3">
          <DashStatTile
            label="Vendas no Período"
            value={metrics ? formatCurrency(metrics.currentValue) : ""}
            icon={faChartLine}
            variant="accent"
            isLoading={chartLoading}
            footnote={`período ${period === "monthly" ? "mensal" : period === "semiannual" ? "semestral" : "anual"}`}
          />
          <DashStatTile
            label="Crescimento"
            value={metrics ? formatPercentage(Math.abs(metrics.growthPercentage)) : ""}
            icon={metrics?.isPositiveGrowth ? faArrowUp : faArrowDown}
            variant={metrics?.isPositiveGrowth ? "good" : "critical"}
            isLoading={chartLoading}
            footnote="vs. período anterior"
          />
          <DashStatTile
            label="Tendência"
            value={metrics ? trendLabel : ""}
            icon={trendIcon}
            variant={trendVariant}
            size="name"
            isLoading={chartLoading}
            footnote="últimos registros"
          />
          <DashStatTile
            label="Melhor Período"
            value={metrics ? metrics.bestPeriod : ""}
            icon={faChartLine}
            variant="accent2"
            size="name"
            isLoading={chartLoading}
            footnote={metrics ? formatCurrency(metrics.bestPeriodValue) : undefined}
          />
        </div>
      )}

      <div style={{ height: isXs ? "250px" : "380px" }}>
        <SalesEvolutionChart period={period} />
      </div>
    </div>
  );
};

export default SalesEvolution;
